import React, { useMemo } from "react";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import { ChartPieSlice, Buildings, Tag } from "@phosphor-icons/react";

const COLORS = ["#E63946", "#7209B7", "#10B981", "#F59E0B", "#3B82F6", "#EC4899", "#8B5CF6", "#14B8A6"];

function tally(items, keyFn) {
  const map = {};
  items.forEach((it) => {
    keyFn(it).forEach((k) => {
      if (!k) return;
      map[k] = (map[k] || 0) + 1;
    });
  });
  return Object.entries(map)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

export default function CollectionStats({ items }) {
  const byPublisher = useMemo(() => tally(items, (m) => [m.publisher]).slice(0, 6), [items]);
  const byGenre = useMemo(() => tally(items, (m) => m.genres || []).slice(0, 8), [items]);

  if (!items || items.length === 0) return null;
  const hasPub = byPublisher.length > 0;
  const hasGenre = byGenre.length > 0;
  if (!hasPub && !hasGenre) return null;

  return (
    <div data-testid="collection-stats">
      <div className="flex items-center gap-2 mb-4">
        <ChartPieSlice size={18} weight="fill" className="text-[#E63946]" />
        <h3 className="font-display text-lg font-bold">Estatísticas da Coleção</h3>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {hasGenre && (
          <StatCard title="Por Género" icon={<Tag size={14} weight="fill" />}>
            <DonutChart data={byGenre} />
          </StatCard>
        )}
        {hasPub && (
          <StatCard title="Por Editora" icon={<Buildings size={14} weight="fill" />}>
            <BarList data={byPublisher} />
          </StatCard>
        )}
      </div>
    </div>
  );
}

function StatCard({ title, icon, children }) {
  return (
    <div className="border border-white/5 rounded-2xl bg-[#0f0f14] p-5">
      <div className="font-mono-accent text-[10px] text-zinc-400 flex items-center gap-1.5 mb-4">
        {icon} {title.toUpperCase()}
      </div>
      {children}
    </div>
  );
}

function DonutChart({ data }) {
  return (
    <div className="flex items-center gap-4">
      <div className="w-32 h-32 flex-shrink-0">
        <PieChart width={128} height={128}>
          <Pie data={data} dataKey="value" nameKey="name" cx={64} cy={64} innerRadius={38} outerRadius={62} paddingAngle={2} stroke="none">
            {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Pie>
          <Tooltip
            contentStyle={{ background: "#18181B", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
            itemStyle={{ color: "#fff" }}
          />
        </PieChart>
      </div>
      <div className="flex-1 min-w-0 space-y-1.5">
        {data.map((d, i) => (
          <div key={d.name} className="flex items-center gap-2 text-xs">
            <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
            <span className="truncate flex-1 text-zinc-300">{d.name}</span>
            <span className="font-mono-accent text-zinc-500">{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BarList({ data }) {
  const max = data[0]?.value || 1;
  return (
    <div className="space-y-3">
      {data.map((d, i) => (
        <div key={d.name}>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="truncate text-zinc-300">{d.name}</span>
            <span className="font-mono-accent text-zinc-500 ml-2">{d.value}</span>
          </div>
          <div className="h-2 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${Math.max(8, (d.value / max) * 100)}%`, background: COLORS[i % COLORS.length] }} />
          </div>
        </div>
      ))}
    </div>
  );
}
